import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Building2, CheckCircle2, UploadCloud, FileText, Sparkles } from "lucide-react";
import {
  REGIONI, ATECO, DIMENSIONI, DIPENDENTI, FATTURATO, FORME_GIURIDICHE, OBIETTIVI,
} from "@/lib/constants";

const empty = {
  ragione_sociale: "", partita_iva: "", ateco: "", settore: "", regione: "",
  sede: "", dimensione: "", dipendenti: "", fatturato: "", forma_giuridica: "",
  obiettivi: [], descrizione: "",
};

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export default function CompanyForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [visuraName, setVisuraName] = useState("");
  const fileRef = useRef(null);
  const [extractingBil, setExtractingBil] = useState(false);
  const [bilancioName, setBilancioName] = useState("");
  const bilancioRef = useRef(null);

  useEffect(() => {
    if (editing) {
      api.get(`/companies/${id}`).then(({ data }) => setForm({ ...empty, ...data }));
    }
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onVisura = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVisuraName(file.name);
    setExtracting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/companies/extract-visura", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      let applied = 0;
      setForm((f) => {
        const next = { ...f };
        Object.entries(data).forEach(([k, v]) => {
          if (v && String(v).trim()) {
            next[k] = v;
            applied += 1;
          }
        });
        return next;
      });
      toast.success(`Dati estratti dalla visura (${applied} campi compilati)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Estrazione non riuscita");
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onBilancio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBilancioName(file.name);
    setExtractingBil(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/companies/extract-bilancio", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      let applied = 0;
      setForm((f) => {
        const next = { ...f };
        Object.entries(data).forEach(([k, v]) => {
          if (v && String(v).trim()) { next[k] = v; applied += 1; }
        });
        return next;
      });
      toast.success(`Dati economici stimati dal bilancio (${applied} campi)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Estrazione non riuscita");
    } finally {
      setExtractingBil(false);
      if (bilancioRef.current) bilancioRef.current.value = "";
    }
  };

  const toggleObj = (o) =>
    setForm((f) => ({
      ...f,
      obiettivi: f.obiettivi.includes(o)
        ? f.obiettivi.filter((x) => x !== o)
        : [...f.obiettivi, o],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const payload = {
      ragione_sociale: form.ragione_sociale, partita_iva: form.partita_iva,
      ateco: form.ateco, settore: form.settore, regione: form.regione, sede: form.sede,
      dimensione: form.dimensione, dipendenti: form.dipendenti, fatturato: form.fatturato,
      forma_giuridica: form.forma_giuridica, obiettivi: form.obiettivi, descrizione: form.descrizione,
    };
    try {
      if (editing) {
        await api.put(`/companies/${id}`, payload);
        toast.success("Azienda aggiornata");
      } else {
        await api.post("/companies", payload);
        toast.success("Azienda creata");
      }
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <button onClick={() => navigate("/app")} className="mb-4 flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Torna alle aziende
      </button>

      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white">
          <Building2 className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">
            {editing ? "Modifica azienda" : "Nuova azienda"}
          </h1>
          <p className="text-sm text-slate-500">Più dati inserisci, più preciso sarà il matching dell'AI.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-6">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
              <Sparkles className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <h2 className="font-display text-base font-bold text-slate-900">Compila automaticamente dai documenti</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Carica la visura camerale e/o il bilancio (PDF o immagine): l'AI compilerà i campi
                anagrafici e stimerà fatturato e dimensione.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={onVisura}
                data-testid="visura-file-input"
              />
              <input
                ref={bilancioRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={onBilancio}
                data-testid="bilancio-file-input"
              />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2 bg-white"
                  onClick={() => fileRef.current?.click()}
                  disabled={extracting}
                  data-testid="visura-upload-button"
                >
                  {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                  {extracting ? "Lettura visura..." : "Carica visura"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2 bg-white"
                  onClick={() => bilancioRef.current?.click()}
                  disabled={extractingBil}
                  data-testid="bilancio-upload-button"
                >
                  {extractingBil ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                  {extractingBil ? "Lettura bilancio..." : "Carica bilancio"}
                </Button>
              </div>
              <div className="mt-2 space-y-1">
                {visuraName && !extracting && (
                  <span className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="visura-filename">
                    <FileText className="h-3.5 w-3.5" />Visura: {visuraName}
                  </span>
                )}
                {bilancioName && !extractingBil && (
                  <span className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="bilancio-filename">
                    <FileText className="h-3.5 w-3.5" />Bilancio: {bilancioName}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-base font-bold text-slate-900">Dati anagrafici</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Ragione Sociale *">
              <Input data-testid="profile-name-input" value={form.ragione_sociale}
                onChange={(e) => set("ragione_sociale", e.target.value)} required placeholder="Es. Rossi Impianti SRL" />
            </Field>
            <Field label="Partita IVA">
              <Input data-testid="profile-piva-input" value={form.partita_iva}
                onChange={(e) => set("partita_iva", e.target.value)} placeholder="IT01234567890" />
            </Field>
            <Field label="Codice ATECO *">
              <Select value={form.ateco} onValueChange={(v) => set("ateco", v)}>
                <SelectTrigger data-testid="profile-ateco-select"><SelectValue placeholder="Seleziona ATECO" /></SelectTrigger>
                <SelectContent>
                  {ATECO.map((a) => <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Settore / Attività">
              <Input data-testid="profile-sector-input" value={form.settore}
                onChange={(e) => set("settore", e.target.value)} placeholder="Es. Impiantistica industriale" />
            </Field>
            <Field label="Forma Giuridica *">
              <Select value={form.forma_giuridica} onValueChange={(v) => set("forma_giuridica", v)}>
                <SelectTrigger data-testid="profile-legal-form-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {FORME_GIURIDICHE.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-base font-bold text-slate-900">Dimensione & territorio</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Regione *">
              <Select value={form.regione} onValueChange={(v) => set("regione", v)}>
                <SelectTrigger data-testid="profile-region-select"><SelectValue placeholder="Seleziona regione" /></SelectTrigger>
                <SelectContent>
                  {REGIONI.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sede operativa (città)">
              <Input data-testid="profile-city-input" value={form.sede}
                onChange={(e) => set("sede", e.target.value)} placeholder="Es. Milano" />
            </Field>
            <Field label="Dimensione aziendale *">
              <Select value={form.dimensione} onValueChange={(v) => set("dimensione", v)}>
                <SelectTrigger data-testid="profile-size-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {DIMENSIONI.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Numero dipendenti *">
              <Select value={form.dipendenti} onValueChange={(v) => set("dipendenti", v)}>
                <SelectTrigger data-testid="profile-employees-input"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {DIPENDENTI.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Fatturato annuo *">
              <Select value={form.fatturato} onValueChange={(v) => set("fatturato", v)}>
                <SelectTrigger data-testid="profile-revenue-input"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {FATTURATO.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-base font-bold text-slate-900">Obiettivi strategici</h2>
          <p className="mt-1 text-sm text-slate-500">Seleziona ciò su cui vuoi investire: guida il matching dei bandi.</p>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="profile-objectives">
            {OBIETTIVI.map((o) => {
              const active = form.obiettivi.includes(o);
              return (
                <button key={o} type="button" onClick={() => toggleObj(o)}
                  data-testid={`objective-${o}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                    active ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}>
                  {active && <CheckCircle2 className="h-3.5 w-3.5" />}
                  {o}
                </button>
              );
            })}
          </div>
          <div className="mt-5">
            <Field label="Note aggiuntive (facoltativo)">
              <Textarea data-testid="profile-notes-input" value={form.descrizione}
                onChange={(e) => set("descrizione", e.target.value)} rows={3}
                placeholder="Descrivi progetti, investimenti previsti o esigenze specifiche..." />
            </Field>
          </div>
        </div>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="form-error">{error}</p>}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/app")}>Annulla</Button>
          <Button type="submit" className="gap-2" disabled={loading} data-testid="profile-save-button">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? "Salva modifiche" : "Crea azienda"}
          </Button>
        </div>
      </form>
    </div>
  );
}
